export type MentalStatus = 'alert' | 'drowsy' | 'stupor' | 'coma' | 'unknown'
export type UrineKetones = 0 | 0.5 | 1 | 2 | 3 | 4
export type BloodGasSample = 'venous' | 'arterial'
export type Diagnosis = 'DKA' | 'EUGLYCEMIC_DKA' | 'HHS' | 'MIXED_DKA_HHS' | 'UNCLASSIFIED'
export type AlertLevel = 'critical' | 'warning' | 'info'
export type ProvisionalPatternKind =
  | 'DKA_POSSIBLE_NO_KETONES'
  | 'HHS_POSSIBLE_NO_KETONES'
  | 'MIXED_POSSIBLE_NO_KETONES'
  | 'UNSPECIFIED_NO_KETONES'

export interface ClinicalInput {
  weightKg?: number
  knownDiabetes: boolean
  sglt2Inhibitor?: boolean
  frailty?: boolean
  mentalStatus?: MentalStatus
  heartFailure?: boolean
  advancedKidneyDisease?: boolean
  dialysis?: boolean
  pregnancy?: boolean
  glucoseMgDl?: number
  sodiumMmolL?: number
  potassiumMmolL?: number
  chlorideMmolL?: number
  bicarbonateMmolL?: number
  ph?: number
  bloodGasSample?: BloodGasSample
  betaHydroxybutyrateMmolL?: number
  urineKetones?: UrineKetones
  ureaMmolL?: number
  measuredOsmolality?: number
  urineOutputMlKgH?: number
  cognitionImproved?: boolean
}

export interface CriteriaState {
  met: boolean | null
  label: string
  detail: string
}

export interface EngineAlert {
  id: string
  level: AlertLevel
  title: string
  message: string
}

export interface ProvisionalPattern {
  kind: ProvisionalPatternKind
  label: string
  message: string
}

export interface EngineResult {
  diagnosis: Diagnosis
  provisionalPattern?: ProvisionalPattern
  dka: { D: CriteriaState; K: CriteriaState; A: CriteriaState; met: boolean }
  hhs: { G: CriteriaState; O: CriteriaState; K: CriteriaState; A: CriteriaState; met: boolean }
  calculations: {
    anionGap?: number
    effectiveOsmolality?: number
    totalOsmolality?: number
  }
  dkaSeverity?: {
    band: 'mild' | 'moderate' | 'severe' | 'indeterminate'
    reasons: string[]
    note: string
  }
  insulin: {
    allowed: boolean | null
    suggestedUnitsHour?: number
    suggestedUnitsKgHour?: number
    rationale?: string
  }
  resolution: {
    dka: boolean | null
    hhs: boolean | null
  }
  alerts: EngineAlert[]
  standardFluidProtocolEligible: boolean
}

const finite = (value: number | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const round = (value: number, digits = 1) => {
  const p = 10 ** digits
  return Math.round(value * p) / p
}

const urineKetoneLabel = (value: UrineKetones) => {
  if (value === 0) return 'negativi'
  if (value === 0.5) return 'tracce'
  return `${value}+`
}

const bloodGasSampleLabel = (sample: BloodGasSample | undefined) => sample === 'arterial' ? 'arterioso' : 'venoso'

export function calculateAnionGap(input: ClinicalInput): number | undefined {
  if (!finite(input.sodiumMmolL) || !finite(input.chlorideMmolL) || !finite(input.bicarbonateMmolL)) return undefined
  return round(input.sodiumMmolL - input.chlorideMmolL - input.bicarbonateMmolL, 1)
}

export function calculateEffectiveOsmolality(input: ClinicalInput): number | undefined {
  if (!finite(input.sodiumMmolL) || !finite(input.glucoseMgDl)) return undefined
  return round(2 * input.sodiumMmolL + input.glucoseMgDl / 18, 1)
}

export function calculateTotalOsmolality(input: ClinicalInput): number | undefined {
  const effective = calculateEffectiveOsmolality(input)
  if (!finite(effective) || !finite(input.ureaMmolL)) return undefined
  return round(effective + input.ureaMmolL, 1)
}

function ketonePositiveForDka(input: ClinicalInput): boolean | null {
  if (finite(input.betaHydroxybutyrateMmolL)) return input.betaHydroxybutyrateMmolL >= 3
  if (finite(input.urineKetones)) return input.urineKetones >= 2
  return null
}

function ketoneLowForHhs(input: ClinicalInput): boolean | null {
  if (finite(input.betaHydroxybutyrateMmolL)) return input.betaHydroxybutyrateMmolL < 3
  if (finite(input.urineKetones)) return input.urineKetones < 2
  return null
}

function state(met: boolean | null, label: string, detail: string): CriteriaState {
  return { met, label, detail }
}

function classifyDkaSeverity(input: ClinicalInput): EngineResult['dkaSeverity'] {
  const ranks: Array<{ rank: number; label: 'mild' | 'moderate' | 'severe'; reason: string }> = []

  if (finite(input.betaHydroxybutyrateMmolL) && input.betaHydroxybutyrateMmolL > 6) {
    ranks.push({ rank: 3, label: 'severe', reason: `β-OHB ${input.betaHydroxybutyrateMmolL} mmol/L` })
  }
  if (finite(input.ph)) {
    if (input.ph < 7) ranks.push({ rank: 3, label: 'severe', reason: `pH ${input.ph}` })
    else if (input.ph <= 7.25) ranks.push({ rank: 2, label: 'moderate', reason: `pH ${input.ph}` })
    else if (input.ph < 7.3) ranks.push({ rank: 1, label: 'mild', reason: `pH ${input.ph}` })
  }
  if (finite(input.bicarbonateMmolL)) {
    if (input.bicarbonateMmolL < 10) ranks.push({ rank: 3, label: 'severe', reason: `HCO₃⁻ ${input.bicarbonateMmolL} mmol/L` })
    else if (input.bicarbonateMmolL < 15) ranks.push({ rank: 2, label: 'moderate', reason: `HCO₃⁻ ${input.bicarbonateMmolL} mmol/L` })
    else if (input.bicarbonateMmolL < 18) ranks.push({ rank: 1, label: 'mild', reason: `HCO₃⁻ ${input.bicarbonateMmolL} mmol/L` })
  }
  if (input.mentalStatus === 'stupor' || input.mentalStatus === 'coma') {
    ranks.push({ rank: 3, label: 'severe', reason: `stato neurologico: ${input.mentalStatus}` })
  } else if (input.mentalStatus === 'drowsy') {
    ranks.push({ rank: 2, label: 'moderate', reason: 'stato neurologico: sonnolento' })
  }

  if (!ranks.length) {
    return { band: 'indeterminate', reasons: [], note: 'Dati insufficienti per una fascia prudenziale di gravità.' }
  }
  const maxRank = Math.max(...ranks.map((item) => item.rank))
  const winner = ranks.find((item) => item.rank === maxRank)!
  return {
    band: winner.label,
    reasons: ranks.filter((item) => item.rank === maxRank).map((item) => item.reason),
    note: 'Fascia prudenziale di supporto: i parametri possono essere discordanti e il setting assistenziale resta una decisione clinica.'
  }
}

export function evaluateClinicalState(input: ClinicalInput): EngineResult {
  const anionGap = calculateAnionGap(input)
  const effectiveOsmolality = calculateEffectiveOsmolality(input)
  const totalOsmolality = calculateTotalOsmolality(input)

  const d = input.knownDiabetes || (finite(input.glucoseMgDl) ? input.glucoseMgDl >= 200 : false)
  const dKnown = input.knownDiabetes || finite(input.glucoseMgDl)
  const k = ketonePositiveForDka(input)
  const a = finite(input.ph) || finite(input.bicarbonateMmolL)
    ? (finite(input.ph) && input.ph < 7.3) || (finite(input.bicarbonateMmolL) && input.bicarbonateMmolL < 18)
    : null

  const dka = Boolean(dKnown && d && k === true && a === true)

  const hhsG = finite(input.glucoseMgDl) ? input.glucoseMgDl >= 600 : null
  const hhsO = finite(effectiveOsmolality) || finite(totalOsmolality) || finite(input.measuredOsmolality)
    ? (finite(effectiveOsmolality) && effectiveOsmolality > 300) ||
      (finite(totalOsmolality) && totalOsmolality > 320) ||
      (finite(input.measuredOsmolality) && input.measuredOsmolality > 320)
    : null
  const hhsK = ketoneLowForHhs(input)
  const hhsA = finite(input.ph) && finite(input.bicarbonateMmolL)
    ? input.ph >= 7.3 && input.bicarbonateMmolL >= 15
    : null
  const hhs = hhsG === true && hhsO === true && hhsK === true && hhsA === true

  const hyperosmolarPattern = hhsG === true && hhsO === true
  const ketonesAvailable = finite(input.betaHydroxybutyrateMmolL) || finite(input.urineKetones)
  let provisionalPattern: ProvisionalPattern | undefined

  if (!ketonesAvailable) {
    if (hyperosmolarPattern && a === true) {
      provisionalPattern = {
        kind: 'MIXED_POSSIBLE_NO_KETONES',
        label: 'Pattern iperosmolare con acidosi · forma mista da escludere',
        message: 'Iperglicemia/iperosmolarità e acidosi sono presenti, ma senza una misura dei chetoni non è possibile confermare DKA né una forma mista DKA/HHS.'
      }
    } else if (dKnown && d && a === true) {
      provisionalPattern = {
        kind: 'DKA_POSSIBLE_NO_KETONES',
        label: 'Pattern acidotico compatibile con DKA · non confermata',
        message: 'Diabete/iperglicemia e acidosi sono presenti, ma manca il criterio obbligatorio di chetosi. Considerare anche cause alternative di acidosi metabolica.'
      }
    } else if (hyperosmolarPattern && hhsA === true) {
      provisionalPattern = {
        kind: 'HHS_POSSIBLE_NO_KETONES',
        label: 'Pattern iperosmolare compatibile con HHS · non confermato',
        message: 'I criteri glicemico, osmolare e acido-base sono compatibili con HHS, ma l’assenza di chetosi significativa non è documentabile senza una misura dei chetoni.'
      }
    } else {
      provisionalPattern = {
        kind: 'UNSPECIFIED_NO_KETONES',
        label: 'Crisi iperglicemica non completamente classificabile',
        message: 'I chetoni non sono disponibili: DKA, HHS e forma mista non possono essere confermate o escluse con certezza dai dati attuali.'
      }
    }
  }

  let diagnosis: Diagnosis = 'UNCLASSIFIED'
  if (dka && hyperosmolarPattern) diagnosis = 'MIXED_DKA_HHS'
  else if (dka && finite(input.glucoseMgDl) && input.glucoseMgDl < 200) diagnosis = 'EUGLYCEMIC_DKA'
  else if (dka) diagnosis = 'DKA'
  else if (hhs) diagnosis = 'HHS'

  const venousPhResolved = finite(input.ph) && (input.bloodGasSample ?? 'venous') === 'venous' && input.ph >= 7.3
  const bicarbonateResolved = finite(input.bicarbonateMmolL) && input.bicarbonateMmolL >= 18
  const dkaResolved = finite(input.betaHydroxybutyrateMmolL) && (finite(input.ph) || finite(input.bicarbonateMmolL))
    ? input.betaHydroxybutyrateMmolL < 0.6 && (venousPhResolved || bicarbonateResolved)
    : null

  const osmolalityForResolution = finite(input.measuredOsmolality)
    ? input.measuredOsmolality
    : (finite(totalOsmolality) ? totalOsmolality : effectiveOsmolality)
  const hhsResolved = finite(osmolalityForResolution) && finite(input.glucoseMgDl) && finite(input.urineOutputMlKgH) && typeof input.cognitionImproved === 'boolean'
    ? osmolalityForResolution < 300 && input.glucoseMgDl < 250 && input.urineOutputMlKgH > 0.5 && input.cognitionImproved
    : null

  const alerts: EngineAlert[] = []
  let insulinAllowed: boolean | null = null
  let insulinRate: number | undefined
  let insulinUnitsKgHour: number | undefined
  let insulinRationale: string | undefined

  if (finite(input.potassiumMmolL)) {
    insulinAllowed = input.potassiumMmolL >= 3.5
    if (input.potassiumMmolL < 3.5) {
      alerts.push({
        id: 'K-LOW-INSULIN-BLOCK',
        level: 'critical',
        title: 'Potassio <3,5 mmol/L',
        message: 'Il percorso standard prevede di correggere l’ipokaliemia e rinviare l’insulina finché K non supera 3,5 mmol/L.'
      })
    }
  }

  if (insulinAllowed !== false && finite(input.weightKg)) {
    if (diagnosis === 'DKA' || diagnosis === 'EUGLYCEMIC_DKA' || diagnosis === 'MIXED_DKA_HHS') {
      const glucoseBelow250 = finite(input.glucoseMgDl) && input.glucoseMgDl < 250
      insulinUnitsKgHour = glucoseBelow250 ? 0.05 : 0.1
      insulinRate = round(input.weightKg * insulinUnitsKgHour, 1)
      insulinRationale = glucoseBelow250
        ? 'DKA ancora attiva con glicemia <250 mg/dL: mantenere l’azione insulinica con apporto glucidico secondo protocollo.'
        : 'Velocità di riferimento per DKA / forma mista.'
    } else if (diagnosis === 'HHS') {
      insulinUnitsKgHour = 0.05
      insulinRate = round(input.weightKg * insulinUnitsKgHour, 1)
      insulinRationale = 'Velocità di riferimento per HHS senza acidosi significativa.'
    }
  }

  if ((diagnosis === 'DKA' || diagnosis === 'EUGLYCEMIC_DKA' || diagnosis === 'MIXED_DKA_HHS') && dkaResolved === false && finite(input.glucoseMgDl) && input.glucoseMgDl < 250) {
    alerts.push({
      id: 'DKA-GLUCOSE-BELOW-250',
      level: 'warning',
      title: 'Glicemia <250 mg/dL ma DKA non risolta',
      message: 'Valutare glucosio EV e prosecuzione dell’insulina a velocità ridotta secondo protocollo, fino alla risoluzione della chetoacidosi.'
    })
  }

  if (input.sglt2Inhibitor && (diagnosis === 'DKA' || diagnosis === 'EUGLYCEMIC_DKA' || diagnosis === 'MIXED_DKA_HHS')) {
    alerts.push({
      id: 'SGLT2-DKA-CONTEXT',
      level: 'warning',
      title: 'SGLT2-inibitore presente',
      message: 'Il trattamento con SGLT2-inibitore è un contesto rilevante soprattutto nella DKA euglicemica; rivalutare il farmaco e il fattore precipitante secondo protocollo clinico.'
    })
  }

  if (input.sglt2Inhibitor && !ketonesAvailable && a === true) {
    alerts.push({
      id: 'SGLT2-KETONES-UNAVAILABLE',
      level: 'warning',
      title: 'SGLT2-inibitore + acidosi con chetoni non disponibili',
      message: 'Una DKA, anche euglicemica, non può essere esclusa senza una misura dei chetoni. Cercare β-OHB capillare/laboratorio o chetonuria appena possibile.'
    })
  }

  const standardFluidProtocolEligible = !Boolean(
    input.heartFailure || input.advancedKidneyDisease || input.dialysis || input.pregnancy || input.frailty
  )
  if (!standardFluidProtocolEligible) {
    alerts.push({
      id: 'FLUID-HIGH-RISK',
      level: 'warning',
      title: 'Fluidoterapia da individualizzare',
      message: 'È presente una condizione che rende inappropriata l’applicazione automatica dello schema standard dei fluidi.'
    })
  }

  if (!finite(input.betaHydroxybutyrateMmolL) && finite(input.urineKetones)) {
    alerts.push({
      id: 'BOHB-NOT-AVAILABLE',
      level: 'info',
      title: 'β-idrossibutirrato non disponibile',
      message: 'La chetonuria può supportare la diagnosi, ma non deve essere usata per dichiarare la risoluzione della DKA.'
    })
  }

  if (!ketonesAvailable) {
    alerts.push({
      id: 'KETONES-UNAVAILABLE',
      level: 'warning',
      title: 'Chetoni non disponibili',
      message: 'Impossibile discriminare con certezza DKA, HHS e forma mista. Ricercare β-OHB capillare/di laboratorio o chetonuria appena possibile; nel frattempo usare quadro clinico e protocollo locale.'
    })
    if (finite(anionGap) && anionGap > 12 && a === true) {
      alerts.push({
        id: 'HIGH-AG-NO-KETONES',
        level: 'info',
        title: 'Anion gap elevato con chetoni non disponibili',
        message: `AG ${anionGap} mmol/L: supporta la presenza di un’acidosi ad anion gap elevato, ma non sostituisce la misura dei chetoni e non distingue DKA da altre cause.`
      })
    }
  }

  const ketoneDetail = finite(input.betaHydroxybutyrateMmolL)
    ? `β-OHB ${input.betaHydroxybutyrateMmolL} mmol/L`
    : finite(input.urineKetones)
      ? `Chetonuria ${urineKetoneLabel(input.urineKetones)}`
      : 'Chetoni non disponibili'
  const phDetail = finite(input.ph) ? `pH ${input.ph} (${bloodGasSampleLabel(input.bloodGasSample)})` : 'pH —'

  return {
    diagnosis,
    provisionalPattern,
    dka: {
      D: state(dKnown ? d : null, 'D', input.knownDiabetes ? 'Diabete noto' : finite(input.glucoseMgDl) ? `Glicemia ${input.glucoseMgDl} mg/dL` : 'Dato non disponibile'),
      K: state(k, 'K', ketoneDetail),
      A: state(a, 'A', finite(input.ph) || finite(input.bicarbonateMmolL) ? `${phDetail}; HCO₃⁻ ${input.bicarbonateMmolL ?? '—'}` : 'Dato non disponibile'),
      met: dka
    },
    hhs: {
      G: state(hhsG, 'G', finite(input.glucoseMgDl) ? `Glicemia ${input.glucoseMgDl} mg/dL` : 'Dato non disponibile'),
      O: state(hhsO, 'O', finite(effectiveOsmolality) ? `Osm eff ${effectiveOsmolality} mOsm/kg` : 'Dato non disponibile'),
      K: state(hhsK, 'K', ketoneDetail),
      A: state(hhsA, 'A', finite(input.ph) && finite(input.bicarbonateMmolL) ? `${phDetail}; HCO₃⁻ ${input.bicarbonateMmolL}` : 'Servono pH e HCO₃⁻'),
      met: hhs
    },
    calculations: { anionGap, effectiveOsmolality, totalOsmolality },
    dkaSeverity: dka ? classifyDkaSeverity(input) : undefined,
    insulin: {
      allowed: insulinAllowed,
      suggestedUnitsHour: insulinRate,
      suggestedUnitsKgHour: insulinUnitsKgHour,
      rationale: insulinRationale
    },
    resolution: { dka: dkaResolved, hhs: hhsResolved },
    alerts,
    standardFluidProtocolEligible
  }
}
